import { Progress, ProgressPlaceholder } from 'components/progressBar/ProgressBar.styles';
import { PaymentButtonValues } from 'consts';
import { splitAmount } from 'helpers/commonHelper';
import withContext from 'hoc/withContext';
import React, { ReactElement, RefObject, TouchEvent, useEffect, useState } from 'react';
import { IStatusBar } from 'types/Payment';

import { noop } from 'lodash';

import {
  BottomContainer,
  ButtonContainer,
  Container,
  PayButtonWrapper,
  ProgressContainer,
  WrapperBottom,
  WrapperTop,
} from './StatusbarMobile.styles';
import PayButton from '../payButton/PayButton';
import ShortDescription from './shortDescription/ShortDescription';
import OrderDescription from './orderDescription/OrderDescription';
import {
  getOrderDescriptionCssBottomPosition,
  getShortDescriptionCssBottomPosition,
} from './StatusMobileBottomPositionUtil';
import { MobileStatusPaymentData } from './StatusbarMobile.types';
import { AppState, PROGRESS } from '../../config/config';
import withAppStateContext from '../../hoc/withAppStateContext';

const EXTERNAL_BUTTON_INIT_HEIGHT_STATE = 260;
const PROGRESS_INIT_HEIGHT_STATE = 4;
const START_POSITION_INIT_STATE = 0;
const PAY_BUTTON_WRAPPER_INIT_HEIGHT_STATE = 0;
const CONCEALABLE_WRAPPER_INIT_HEIGHT_STATE = 0;

function isValidProgressValue(progress: number | undefined): boolean {
  return progress !== undefined && (progress === PaymentButtonValues.SHOW || progress === PaymentButtonValues.LOADING);
}
function StatusBarMobile(props: IStatusBar): ReactElement {
  const [width, setWidth] = React.useState(window.innerWidth);
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [startPosition, setStartPosition] = useState<number>(START_POSITION_INIT_STATE);
  const [externalButtonHeight, setExternalButtonHeight] = useState<number>(EXTERNAL_BUTTON_INIT_HEIGHT_STATE);
  const [payButtonWrapperHeight, setPayButtonWrapperHeight] = useState<number>(PAY_BUTTON_WRAPPER_INIT_HEIGHT_STATE);
  const [progressElementHeight, setProgressElementHeight] = useState<number>(PROGRESS_INIT_HEIGHT_STATE);
  const [concealableWrapperElementHeight, setConcealableWrapperElementHeight] = useState<number>(
    CONCEALABLE_WRAPPER_INIT_HEIGHT_STATE
  );
  const [error, setError] = useState<Error | undefined>(undefined);

  const button: RefObject<HTMLButtonElement> = React.createRef();
  const descriptionElement: RefObject<HTMLParagraphElement> = React.createRef();
  const bottomElement: RefObject<HTMLDivElement> = React.createRef();
  const payButtonWrapperHeightElement: RefObject<HTMLDivElement> = React.createRef();
  const progressElement: RefObject<HTMLDivElement> = React.createRef();
  const concealableWrapperElement: RefObject<HTMLDivElement> = React.createRef();

  if (error) {
    throw error;
  }

  const { progress, appState } = props;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isValidProgressValue(progress) && isValid) {
        setIsValid(false);
      }

      if (isValidProgressValue(progress) && !isValid) {
        setIsValid(true);
        setIsOpened(appState === AppState.PAYMENT);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [props, progress]);

  const handleResize = () => {
    setWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize, false);
  }, []);

  const setHeight = (element: RefObject<HTMLElement>, setter: (a: number) => void) => {
    if (element.current) {
      setter(element.current.clientHeight);
    }
  };

  useEffect(() => {
    setHeight(bottomElement, setExternalButtonHeight);
  }, [isOpened, isValid, props, width]);

  useEffect(() => {
    setHeight(progressElement, setProgressElementHeight);
  }, [isOpened, isValid, props, width]);

  useEffect(() => {
    setHeight(concealableWrapperElement, setConcealableWrapperElementHeight);
  }, [isOpened, isValid, props, width]);

  useEffect(() => {
    if (progress === PaymentButtonValues.SHOW && button.current) {
      button.current.focus();
    }
  }, [props]);

  const setOrderDescriptionVisibility = (visible: boolean) => {
    setIsOpened(visible);
  };

  const triggerShowPaymentRedirectScreen = () => {
    const { showPaymentRedirectScreen } = props;
    if (showPaymentRedirectScreen) {
      showPaymentRedirectScreen();
    }
  };

  const handleError = (handledError: Error) => {
    setError(handledError);
  };

  const getWrapperTopCssBottomPosition = () =>
    getShortDescriptionCssBottomPosition({
      isOpened,
      isValid,
      externalButtonHeight,
      payButtonWrapperHeight,
      concealableWrapperElementHeight,
      progressElementHeight,
    });

  const getWrapperBottomCssBottomPosition = () =>
    getOrderDescriptionCssBottomPosition({
      isOpened,
      isValid,
      progressElementHeight,
      externalButtonHeight,
      payButtonWrapperHeight,
      concealableWrapperElementHeight: 0,
    });

  const clickHandler = () => {
    setIsOpened(!isOpened);
  };

  const touchHandler = (e: TouchEvent) => {
    const tmpDelta = startPosition - e.touches[0].pageY;
    setIsMoving(!!tmpDelta);
  };

  const touchStartHandler = (e: TouchEvent) => {
    setStartPosition(e.touches[0].pageY);
  };

  const touchEndHandler = () => {
    setIsMoving(false);
  };

  const { payment, paymentMethodId, paymentId, paymentMethodType, showPaymentNotification } = props;
  if (!payment || Object.keys(payment).length === 0) {
    return <></>;
  }

  const { amount = 0, currency, description, shop = {}, buyer = {} } = payment;
  const { name: shopName } = shop;
  const { email: buyerEmail } = buyer;
  const { integers, decimals } = splitAmount(amount);
  const paymentData = {
    payment,
    paymentId,
    paymentMethodId,
    paymentMethodType,
  } as MobileStatusPaymentData;

  return (
    <Container
      onClick={clickHandler}
      onTouchStart={touchStartHandler}
      onTouchMove={touchHandler}
      onTouchEnd={touchEndHandler}
      className="statusbar-mobile__container"
      data-testid="statusbar-mobile:container"
    >
      <WrapperTop bottomHeight={getWrapperTopCssBottomPosition()} isMoving={isMoving}>
        <ShortDescription
          progress={progress || PROGRESS.NOT_SELECTED_PAYMENT_METHOD}
          integers={integers}
          decimals={decimals}
          currency={currency || ''}
          paymentData={paymentData}
          concealableWrapperRef={concealableWrapperElement}
          shopName={shopName || ''}
        />
      </WrapperTop>
      <BottomContainer
        bottomHeight={getWrapperBottomCssBottomPosition()}
        isOpened={isOpened}
        isMoving={isMoving}
        className="statusbar-mobile__bottom-container"
      >
        <WrapperBottom>
          <OrderDescription
            shopName={shopName || ''}
            description={description || ''}
            buyerEmail={buyerEmail || ''}
            elementRef={bottomElement}
            descriptionElementRef={descriptionElement}
          />

          <ButtonContainer isValid={isValid} payButtonWrapperHeight={payButtonWrapperHeight}>
            <PayButtonWrapper ref={payButtonWrapperHeightElement}>
              <PayButton
                {...paymentData}
                paymentButtonRef={button}
                handleError={handleError}
                setLoading={noop}
                showPaymentNotification={showPaymentNotification}
                showPaymentRedirectScreen={triggerShowPaymentRedirectScreen}
                setOrderDescriptionVisibility={setOrderDescriptionVisibility}
                setPayButtonComponentHeight={setPayButtonWrapperHeight}
              />
            </PayButtonWrapper>
          </ButtonContainer>
        </WrapperBottom>
      </BottomContainer>
      <ProgressContainer ref={progressElement}>
        <Progress progress={progress} mobile className="statusbar-mobile__progressbar" />
        <ProgressPlaceholder dark />
      </ProgressContainer>
    </Container>
  );
}

export default withContext(withAppStateContext(StatusBarMobile));
